import { getAllByIndex, putRecord } from './db';
import { getDeviceId } from './device';
import { featureMode } from './featureMatrix';
import type { OfflineAuthorizationEnvelope } from './types';

export async function saveOfflineAuthorizationEnvelope(envelope: OfflineAuthorizationEnvelope): Promise<void> {
  if (envelope.device_id !== getDeviceId()) throw new Error('Offline authorization envelope is not bound to this device');
  await putRecord('offline_auth_envelopes', envelope);
}

export async function currentOfflineAuthorizationEnvelope(userId: string): Promise<OfflineAuthorizationEnvelope | null> {
  const deviceId = getDeviceId();
  const rows = await getAllByIndex<OfflineAuthorizationEnvelope>('offline_auth_envelopes', 'byUserDevice', IDBKeyRange.only([userId, deviceId]));
  return rows.sort((a, b) => String(b.issued_at).localeCompare(String(a.issued_at)))[0] || null;
}

export function envelopeValid(envelope: OfflineAuthorizationEnvelope | null, now = Date.now()): boolean {
  if (!envelope || envelope.credential_status_snapshot !== 'ACTIVE') return false;
  const expiry = Date.parse(envelope.offline_valid_until);
  return Number.isFinite(expiry) && expiry > now;
}

export async function offlineActionDecision(userId: string, action: string): Promise<{ allowed: boolean; reason: string; envelope: OfflineAuthorizationEnvelope | null }> {
  const mode = featureMode(action);
  if (mode === 'ONLINE_REQUIRED') return { allowed: false, reason: `${action} is classified ONLINE_REQUIRED`, envelope: null };
  const envelope = await currentOfflineAuthorizationEnvelope(userId);
  if (!envelope) return { allowed: false, reason: 'No offline authorization envelope is available on this device', envelope: null };
  if (!envelopeValid(envelope)) return { allowed: false, reason: 'Offline authorization envelope has expired or is restricted', envelope };
  if (!envelope.offline_allowed_actions.includes(action)) return { allowed: false, reason: `Offline authorization does not permit ${action}`, envelope };
  return { allowed: true, reason: 'PERMITTED', envelope };
}

export async function clearOfflineAuthorizationForUser(userId: string): Promise<void> {
  const deviceId = getDeviceId();
  const rows = await getAllByIndex<OfflineAuthorizationEnvelope>('offline_auth_envelopes', 'byUserDevice', IDBKeyRange.only([userId, deviceId]));
  const { deleteRecord } = await import('./db');
  for (const row of rows) await deleteRecord('offline_auth_envelopes', row.envelope_id);
}
